import Header from "~/components/ui/SectionHeader"
import type { ImageWidget } from "~/types/widgets"
import { Picture, Source } from "~/components/ui/Picture"

/**
 * @titleBy alt
 */
export interface Banner {
    /**
     * @title Imagem mobile
     */
    srcMobile: ImageWidget
    /**
     * @title Imagem desktop
     */
    srcDesktop?: ImageWidget
    /**
     * @title Texto alternativo
     */
    alt: string
    /**
     * @title Link
     */
    href: string
}

export type BorderRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full'

export interface Props {
    /**
     * @title Título
     */
    title?: string
    /**
     * @title Descrição
     */
    description?: string
    /**
     * @title Banners
     * @maxItems 4
     * @minItems 4
     */
    banners?: Banner[]
    layout?: {
        /**
         * @description Aplique borda a sua imagem
         */
        borderRadius?: {
            /** @default none */
            mobile?: BorderRadius
            /** @default none */
            desktop?: BorderRadius
        }
        /**
         * @title Alinhamento do cabeçalho
         */
        headerAlignment?: 'center' | 'left'
        /**
         * @title Simetria Mobile
         */
        mobile?: 'Asymmetric' | 'Symmetrical'
        /**
         * @title Simetria Desktop
         */
        desktop?: 'Asymmetric' | 'Symmetrical'
    }
}

const RADIUS: Record<string, Record<BorderRadius, string>> = {
    mobile: {
        none: 'rounded-none',
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        '2xl': 'rounded-2xl',
        '3xl': 'rounded-3xl',
        full: 'rounded-full',
    },
    desktop: {
        none: 'sm:rounded-none',
        sm: 'sm:rounded-sm',
        md: 'sm:rounded-md',
        lg: 'sm:rounded-lg',
        xl: 'sm:rounded-xl',
        '2xl': 'sm:rounded-2xl',
        '3xl': 'sm:rounded-3xl',
        full: 'sm:rounded-full',
    },
}

const DEFAULT_PROPS: Props = {
    banners: [
        {
            srcMobile:
                'https://decoims.com/miess-01/41a841fb-672c-4c38-b1e1-4fccc09b80e0/b531631b-8523-4feb-ac37-5112873abad2.jpg',
            srcDesktop:
                'https://decoims.com/miess-01/41a841fb-672c-4c38-b1e1-4fccc09b80e0/b531631b-8523-4feb-ac37-5112873abad2.jpg',
            alt: 'Fashion',
            href: '/',
        },
        {
            alt: 'Fashion',
            href: '/',
            srcMobile:
                'https://decoims.com/miess-01/b64c734b-f81a-4756-b260-7d9b43bed985/1125d938-89ff-4aae-a354-63d4241394a6.jpg',
            srcDesktop:
                'https://decoims.com/miess-01/b64c734b-f81a-4756-b260-7d9b43bed985/1125d938-89ff-4aae-a354-63d4241394a6.jpg',
        },
        {
            srcMobile:
                'https://decoims.com/miess-01/fc092203-a2d1-454d-8f62-ceee5180938e/dd1e2acb-ff80-49f9-8f56-1deac3b7a42d.jpg',
            srcDesktop:
                'https://decoims.com/miess-01/fc092203-a2d1-454d-8f62-ceee5180938e/dd1e2acb-ff80-49f9-8f56-1deac3b7a42d.jpg',
            href: '/',
            alt: 'Fashion',
        },
        {
            srcMobile:
                'https://decoims.com/miess-01/793fb7d1-9e94-4680-8059-83952f11006a/0b85ba2d-48b1-4f5b-b619-7f4a7f50b455.jpg',
            srcDesktop:
                'https://decoims.com/miess-01/793fb7d1-9e94-4680-8059-83952f11006a/0b85ba2d-48b1-4f5b-b619-7f4a7f50b455.jpg',
            alt: 'Fashion',
            href: '/',
        },
    ],
    layout: {
        borderRadius: {
            mobile: '3xl',
            desktop: '2xl',
        },
        headerAlignment: 'center',
        mobile: 'Asymmetric',
        desktop: 'Asymmetric',
    },
}

function Banner(
    props: Banner & {
        borderRadius?: {
            /** @default none */
            mobile?: BorderRadius
            /** @default none */
            desktop?: BorderRadius
        }
    },
) {
    const { borderRadius, srcMobile, srcDesktop, alt } = props
    const radiusDesktop = RADIUS.desktop[borderRadius?.desktop ?? 'none']
    const radiusMobile = RADIUS.mobile[borderRadius?.desktop ?? 'none']

    return (
        <a href={props.href} className={`overflow-hidden ${radiusDesktop} ${radiusMobile}`}>
            <Picture>
                <Source width={190} height={190} media='(max-width: 767px)' src={srcMobile} />
                <Source
                    width={640}
                    height={420}
                    media='(min-width: 768px)'
                    src={srcDesktop || srcMobile}
                />
                <img
                    className='w-full h-full object-cover'
                    src={srcMobile}
                    alt={alt}
                    decoding='async'
                    loading='lazy'
                />
            </Picture>
        </a>
    )
}

export default function Gallery(props: Props) {
    const { title, description, banners, layout } = {
        ...DEFAULT_PROPS,
        ...props,
    }

    const mobileItemLayout = (index: number) =>
        layout?.mobile === 'Symmetrical'
            ? 'row-span-3'
            : index === 0 || index === 3
            ? 'row-span-3'
            : 'row-span-2'

    const desktopItemLayout = (index: number) =>
        layout?.desktop === 'Symmetrical'
            ? 'sm:row-span-3'
            : index === 0 || index === 3
            ? 'sm:row-span-3'
            : 'sm:row-span-2'

    return (
        <section className='container px-4 py-8 flex flex-col gap-8 lg:gap-10 lg:py-10 lg:px-0'>
            <Header
                title={title}
                description={description}
                alignment={layout?.headerAlignment || 'center'}
            />
            <ul className='grid grid-flow-col grid-cols-2 grid-rows-6 gap-4 list-none'>
                {banners?.map((banner, index) => (
                    <li className={`${mobileItemLayout(index)} ${desktopItemLayout(index)}`}>
                        <Banner {...banner} borderRadius={props.layout?.borderRadius} />
                    </li>
                ))}
            </ul>
        </section>
    )
}
export { default as LoadingFallback } from "~/components/LoadingFallback"
